import spaceService from "./spaceService";
import subscriptionService from "./subscriptionService";
import planService from "./planService";
import networkService from "./networkService";
import venueService from "./venueService";
import Plan from "./plan";
import { venue } from "venuescanner-js/schemas/serviceSchema.json";

import { urls } from "venuescanner-js/services/endPoints";
import subscriptionType from "venuescanner-js/constants/subscriptions";
import Tiers from "venuescanner-js/constants/tiers";
import Chargebee from "../chargebee";
import { availableTrialDays } from "../localStorageServices";

/** @class
 * @name Venue
 * @desc Class definition of a Venue - schema defined in venuescanner-js/schemas/serviceSchema.json
 * @param {Object} [values={}] - The values to apply to the model
 */
class Venue {
  constructor(values = {}) {
    this.applyValues(values);
    this._spaces = null;
  }

  /** @function
   * @name applyValues
   * @memberof Venue
   * @param {Object} values - The values to apply to the model
   * @desc Sets the values in the Model
   */
  applyValues(values = {}) {
    Object.keys(venue).forEach((key) => {
      this[key] = values[key];
    });
  }

  /** @function
   * @name photoURL
   * @memberof Venue
   * @param {string} size - Defaults to min
   * @desc Returns the first photo URL
   * @returns {string}
   */
  photoURL(size = "min") {
    if (this.photos.length) {
      return `${urls.PHOTOS_URL}/${this.hashcode}/${size}_${this.photos[0].filename}`;
    } else {
      return null;
    }
  }

  /** @function
   * @name spaces
   * @memberof Venue
   * @desc Fetches the associated active Spaces in this Venue
   * @returns {Promise<Space[]>}
   */
  async spaces() {
    if (!this._spaces) {
      this._spaces = await spaceService.fetch(this._id);
      this._spaces.forEach((space) => {
        space._venue = this;
      });
    }
    return this._spaces;
  }

  /** @function
   * @name subscription
   * @memberof Venue
   * @desc Fetches the associated Subscription for this Venue
   * @returns {Promise<Subscription>}
   */
  async subscription() {
    if (typeof this._subscription === "undefined")
      this._subscription = this.sub
        ? await subscriptionService.fetch(this.sub)
        : null;
    return this._subscription;
  }

  /** @function
   * @name getNetwork
   * @memberof Venue
   * @desc Fetches the associated Network for this Venue
   * @returns {Promise<Network>}
   */
  async getNetwork() {
    if (typeof this._network === "undefined" && this.network)
      this._network = await networkService.byId(this.network);
    return this._network;
  }

  /** @function
   * @name checkForUpgradeAndUpdate
   * @memberof Venue
   * @desc Polls for changes in the subscription id, then loads the new subscription
   * @param {function} success  - The callback triggered once the Model has detected the update to the Subscription
   * @param {function} timeout  - The callback triggered if the Subscription has not changed within 10 secs
   */
  checkForUpgradeAndUpdate(success = () => {}, timeout = () => {}) {
    const currentValue = this.sub;
    const start = new Date().getTime();
    const maxTime = 30000;
    // let changed = false
    const poll = async () => {
      const data = await venueService.fetchDateOnlyById(this._id);
      this.applyValues(data);
      if (currentValue !== this.sub) {
        // changed = true
        this._subscription = undefined;
        await this.subscription();
        success();
      } else {
        const now = new Date().getTime();
        if (now - maxTime > start) {
          timeout();
        } else {
          setTimeout(poll, 1000);
        }
      }
    };
    poll();
  }

  /** @function
   * @name availableSubscriptionPlans
   * @memberof Venue
   * @desc Fetches the associated Subscription for this Venue
   * @param {Boolean} [monthly=true]  - Return Monthly Plans, otherwise Annual
   * @param {Object} options
   * @param {Integer} options.trialPeriod  - The max Trial period in days
   * @param {Boolean} options.upgradeOnly  - If true return only upgrade plans
   * @returns {Promise<Plan[]>}
   */
  async availableSubscriptionPlans(
    monthly = true,
    { trialPeriod = null, upgradeOnly = false } = {}
  ) {
    if (!trialPeriod) trialPeriod = availableTrialDays.get();
    if (
      this.sub_plan &&
      Object.values(subscriptionType).includes(this.sub_plan)
    ) {
      if (this.sub_plan === subscriptionType.GOLD) {
        // no upgrade available
        return [];
      } else {
        return planService.filter({
          tier: this.tier || Tiers.tier4,
          trialPeriod,
          monthly,
          above: upgradeOnly ? this.sub_plan : null,
        });
      }
    } else {
      return planService.filter({
        tier: this.tier || Tiers.tier4,
        trialPeriod,
        monthly,
      });
    }
  }

  /** @function
   * @name chargebeeUpgrade
   * @memberof Venue
   * @desc Fetches the Venue Network using the Network Service and then starts the Chargebee service
   * @param {Object} options - Required and optional parameters
   * @param {Plan} options.plan - The subscription plan to apply to the Venue
   * @param {string} [options.coupon] - The Chargebee coupon to apply to the purchase
   * @param {function} options.whichAccount - A callback that receives the selectAccount function where receiving true means use the Venue Account otherwise the Network account. Only called if there are both already created.
   * @param {function} options.upgraded - A callback triggered on the Chargebee widget successfully applying the Plan / Subscription and the Venue receiving the webhooks, applying the Plan in Mongo and updating the Model
   * @param {function} options.onSuccess - A callback triggered on the Chargebee widget successfully applying the Plan / Subscription
   * @param {function} options.onClose - A callback triggered on the Chargebee widget being closed
   * @param {function} options.onError - A callback triggered on the Chargebee widget returning an Error
   * @param {function} options.onShow - A callback triggered on the Chargebee widget displaying
   * @returns {Promise<Plan[]>}
   */
  async chargebeeUpgrade({
    plan,
    coupon,
    whichAccount,
    upgraded = () => {},
    onSuccess,
    onClose,
    onError,
    onShow,
  }) {
    if (!(plan instanceof Plan)) {
      throw new Error("Venue upgrade expected Plan to be an instance of Plan");
    }
    const network = await this.getNetwork();
    const chargebee = new Chargebee({
      plan,
      venue: this,
      network,
      coupon,
      whichAccount,
      onSuccess: () => {
        this.checkForUpgradeAndUpdate(upgraded, onError);
        onSuccess();
      },
      onClose,
      onError,
      onShow,
    });
  }
}

export default Venue;
